import * as Sentry from "@sentry/react";

const SENSITIVE_KEY_RE =
  /password|passwd|secret|token|authorization|cookie|session|api[_-]?key|smtp|email_body|message_body|card|payment|stripe/i;

function isSensitiveKey(k: string): boolean {
  return SENSITIVE_KEY_RE.test(k);
}

function redactObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (isSensitiveKey(k)) {
      out[k] = "[Redacted]";
    } else if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      out[k] = redactObject(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function parseSampleRate(raw: string | undefined, fallback: number): number {
  const n = parseFloat(raw ?? "");
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
}

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const environment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  (import.meta.env.DEV ? "development" : "production");
const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

export function initSentry(): void {
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release,

    integrations: [Sentry.browserTracingIntegration()],

    tracesSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      import.meta.env.PROD ? 0.1 : 0,
    ),
    sendDefaultPii: false,

    beforeSend(event) {
      if (event.request?.headers) {
        const h = event.request.headers as Record<string, string>;
        for (const k of Object.keys(h)) {
          if (isSensitiveKey(k)) h[k] = "[Redacted]";
        }
      }
      if (event.request?.cookies !== undefined) {
        (event.request as Record<string, unknown>).cookies = "[Redacted]";
      }
      if (event.request?.data && typeof event.request.data === "object") {
        event.request.data = redactObject(
          event.request.data as Record<string, unknown>,
        );
      }
      const firstVal = event.exception?.values?.[0]?.value ?? "";
      if (firstVal.includes("ResizeObserver loop")) return null;
      if (firstVal.includes("Non-Error promise rejection captured")) return null;
      return event;
    },

    beforeBreadcrumb(bc) {
      if (bc.category === "console" && bc.level === "debug") return null;
      if (bc.category === "navigation" && typeof bc.data?.to === "string") {
        bc.data.to = bc.data.to.replace(
          /([?&])(token|key|secret|auth|password|apikey)=[^&]*/gi,
          "$1$2=[Redacted]",
        );
      }
      return bc;
    },
  });
}

export { Sentry };
